import { useMemo, useState } from "react";
import {
  ChevronDown,
  Shield,
  Lock,
  KeyRound,
  ScrollText,
  FileSearch,
  Users,
  Database,
  ClipboardCheck,
  Clock,
  CheckCircle,
  Layers,
  Workflow,
  Building2,
  AlertTriangle,
  TrendingUp,
  Calculator,
} from "lucide-react";

export default function LexaLawLanding() {
  /**
   * Target: McLennan Ross scale firm
   * This landing page uses an ROI "capacity unlocked" model (not guaranteed revenue).
   * Sliders let partners set assumptions live.
   */

  // -----------------------------
  // Firm size (editable defaults)
  // -----------------------------
  const firm = {
    displayName: "McLennan Ross",
    lawyerRangeLabel: "115–130 lawyers",
    lawyerCountForCalcDefault: 125,
  };

  // -----------------------------
  // Interactive ROI sliders
  // -----------------------------
  const [lawyerCount, setLawyerCount] = useState(firm.lawyerCountForCalcDefault); // 50–200
  const [hoursSavedPerLawyerPerMonth, setHoursSavedPerLawyerPerMonth] = useState(12); // 0–30
  const [blendedRateCAD, setBlendedRateCAD] = useState(325); // 150–900

  const formatCAD = (n) =>
    new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 }).format(n);

  const monthlyHoursUnlocked = useMemo(
    () => lawyerCount * hoursSavedPerLawyerPerMonth,
    [lawyerCount, hoursSavedPerLawyerPerMonth],
  );

  const monthlyCapacityValue = useMemo(
    () => monthlyHoursUnlocked * blendedRateCAD,
    [monthlyHoursUnlocked, blendedRateCAD],
  );

  const annualCapacityValue = useMemo(() => monthlyCapacityValue * 12, [monthlyCapacityValue]);

  // Scenario cards are derived from the slider's "hours saved" number, so they stay coherent.
  // (The “realistic” scenario highlights the current slider value.)
  const scenarioHours = useMemo(() => {
    // Keep these anchored around the current assumption:
    // - conservative: -4 (min 0)
    // - realistic: current
    // - aggressive: +6
    const conservative = Math.max(0, hoursSavedPerLawyerPerMonth - 4);
    const realistic = hoursSavedPerLawyerPerMonth;
    const aggressive = Math.min(30, hoursSavedPerLawyerPerMonth + 6);
    return [
      { label: "Conservative", hoursSaved: conservative, note: "Mostly precedent retrieval + assembly" },
      {
        label: "Realistic",
        hoursSaved: realistic,
        note: "Precedent + drafting + workflow coordination",
        highlight: true,
      },
      { label: "Aggressive", hoursSaved: aggressive, note: "Firm-wide adoption across core workflows" },
    ];
  }, [hoursSavedPerLawyerPerMonth]);

  const scenarios = useMemo(() => {
    return scenarioHours.map((s) => {
      const mHours = lawyerCount * s.hoursSaved;
      const mValue = mHours * blendedRateCAD;
      const yValue = mValue * 12;
      return { ...s, mHours, mValue, yValue };
    });
  }, [scenarioHours, lawyerCount, blendedRateCAD]);

  const headline = {
    eyebrow: "Secure AI Operating System for Law Firms",
    title: "Not a Legal Tool.",
    title2: "Your Firm’s Brain.",
    subhead:
      "A private, permissioned collective intelligence that learns your firm’s precedent, playbooks, and workflows — then assembles drafts and runs execution checklists with security and auditability at the core.",
  };

  const openerPitchLine = useMemo(() => {
    return `“If I could make your firm an extra ${formatCAD(
      annualCapacityValue,
    )}/year in billable capacity without hiring anyone else, would that be worth 30 minutes of your time?”`;
  }, [annualCapacityValue]);

  const securityPillars = [
    {
      icon: Shield,
      title: "Tenant-Isolated Firm Vault",
      description: "Your firm runs in its own private workspace. No cross-tenant memory. No shared data paths.",
    },
    {
      icon: Lock,
      title: "End-to-End Encryption",
      description: "Encryption in transit and at rest. Sensitive artefacts stay within your firm boundary.",
    },
    {
      icon: KeyRound,
      title: "Role-Based Access + Matter Walls",
      description: "Granular permissions by lawyer, team, practice group, and matter — least privilege by default.",
    },
    {
      icon: ClipboardCheck,
      title: "Approval Gates + Full Audit Trail",
      description: "You choose what requires approval. Every action is logged, reviewable, and exportable.",
    },
    {
      icon: Database,
      title: "Data Residency Options",
      description: "Private cloud / dedicated environment options for firms with strict compliance needs.",
    },
    {
      icon: AlertTriangle,
      title: "Privilege-First Guardrails",
      description:
        "Designed to reduce disclosure risk: source-backed outputs, uncertainty flags, and red-flag warnings.",
    },
  ];

  const howItGrows = [
    {
      icon: Layers,
      week: "Phase 1",
      title: "Learns the Firm",
      description:
        "Lexa maps your precedent, clause language, argument structures, and playbooks — across practice groups — with permissions baked in.",
      example:
        '"This motion format matches 14 successful filings by your litigation team. Here are the 3 variations you use depending on forum and judge."',
    },
    {
      icon: FileSearch,
      week: "Phase 2",
      title: "Assembles Work From Precedent",
      description:
        "It stops generating from scratch. It drafts using your internal matter history, templates, memos, and clause libraries — all matter-walled.",
      example:
        '"Built a first draft factum using 6 similar matters, pulled your best-performing headings, and flagged jurisdictional differences for review."',
    },
    {
      icon: Workflow,
      week: "Phase 3+",
      title: "Runs Firm Workflows (With Your Rules)",
      description:
        "Checklists, matter coordination, document sets, filing prep, and client updates — executed with audit logs and approval gates.",
      example:
        '"Closing binder assembled, signature pages tracked, missing director consent flagged, and client update drafted for partner approval."',
    },
  ];

  const partnership = [
    {
      you: "You practice law",
      lexa: "Lexa runs the machine",
      example: "Advise, argue, negotiate, decide strategy",
      example2: "Finds precedent, assembles drafts, coordinates workflows — with permissions and audit logs",
    },
    {
      you: "You set the standard",
      lexa: "Lexa enforces it firm-wide",
      example: "Preferred clauses, memo structure, tone, risk posture",
      example2: "Applies firm playbooks consistently across teams — reduces variance and rework",
    },
    {
      you: "You protect privilege",
      lexa: "Lexa is designed for it",
      example: "Need strict access controls and matter walls",
      example2: "Role-based access, approval gates, secure vault, and traceable actions — built for privileged work",
    },
  ];

  const capabilities = [
    {
      icon: ScrollText,
      title: "Drafts With Your Precedent",
      description:
        "Briefs, motions, memos, agreements, letters — assembled from your internal templates and prior matters, not generic text.",
    },
    {
      icon: FileSearch,
      title: "Finds the Right Work Product Fast",
      description:
        "Search by issue, jurisdiction, judge, opposing counsel, fact pattern, clause, or outcome — then reuse what already won.",
    },
    {
      icon: Users,
      title: "Collective Brain + Local Assistants",
      description:
        "Every lawyer gets a personal assistant, plus practice-area assistants — all connected to the same permissioned firm knowledge graph.",
    },
    {
      icon: ClipboardCheck,
      title: "Matter Checklists That Execute",
      description:
        "Pre-built workflows for common matter types. Tracks missing inputs, assigns tasks, compiles document sets, drafts updates.",
    },
    {
      icon: Clock,
      title: "Turns Chaos Into Time",
      description:
        "Less searching, less re-drafting, fewer handoffs. Partners spend more time on judgment and strategy — less on assembly.",
    },
    {
      icon: Shield,
      title: "Security-First by Design",
      description: "Approval gates, audit trails, role-based access, and firm isolation — not bolted on later.",
    },
  ];

  const whatItDoes = [
    {
      action: "Assembles first drafts from firm precedent",
      description: "Uses prior matters, templates, and playbooks — permissioned by practice group and matter wall",
    },
    {
      action: "Finds and reuses winning arguments",
      description: "Search by issue, forum, judge, opposing counsel, clause, or fact pattern — then reuse sections",
    },
    {
      action: "Builds research memos with citations to firm sources",
      description: "Summarizes records and internal sources, flags uncertainties, links back to the source material",
    },
    {
      action: "Runs checklists and matter workflows",
      description: "Tracks missing inputs, assigns tasks, compiles document sets, drafts status updates for approval",
    },
    {
      action: "Creates a shared firm memory",
      description: "Captures standards and best practices — without leaking across clients or matters",
    },
    {
      action: "Operates with permission + audit trails",
      description: "Approval gates for sensitive actions; logs every step so partners can review exactly what happened",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Hero */}
      <header className="relative min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 overflow-hidden flex items-center justify-center">
        {/* Background blobs */}
        <div className="absolute top-20 right-16 w-80 h-80 bg-indigo-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob" />
        <div className="absolute bottom-16 left-16 w-80 h-80 bg-sky-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000" />

        <div className="relative z-10 text-center px-4 max-w-6xl">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/15 mb-6">
            <Shield className="w-4 h-4 text-emerald-200" />
            <span className="text-sm text-white/90 font-semibold">{headline.eyebrow}</span>
          </div>

          <h1 className="text-6xl md:text-7xl font-bold text-white mb-6 leading-tight">
            {headline.title}
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-300 to-indigo-300">
              {headline.title2}
            </span>
          </h1>

          <p className="text-xl md:text-2xl text-slate-200 max-w-4xl mx-auto leading-relaxed mb-10">
            {headline.subhead}
          </p>

          {/* The “30 minutes” pitch injected into the hero */}
          <div className="max-w-4xl mx-auto mb-10">
            <div className="bg-white/10 border border-white/15 rounded-2xl p-6 text-left">
              <div className="flex items-start gap-4">
                <TrendingUp className="w-7 h-7 text-emerald-300 mt-1" />
                <div>
                  <p className="text-xs uppercase tracking-wider text-white/70 font-semibold mb-2">
                    Our question to you...
                  </p>
                  <p className="text-xl md:text-2xl text-white font-semibold leading-snug">{openerPitchLine}</p>
                  <p className="text-sm text-white/70 mt-3">
                    Capacity estimate below is conservative and depends on adoption, practice mix, and approval rules.
                    The point is magnitude: firm-wide time savings compound fast.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="#roi"
              className="px-10 py-5 bg-white/10 border border-white/20 text-white text-lg font-semibold rounded-full hover:bg-white/15 transition-all flex items-center justify-center gap-2"
            >
              See the ROI Model <Calculator className="w-5 h-5" />
            </a>
          </div>

          <p className="text-sm text-slate-300 mt-6">
            Built for firms where <strong>security and privilege</strong> are non-negotiable.
          </p>
          <p className="text-xs text-slate-400 mt-2">
            No public training on firm data. Firm-isolated by default. Full audit trails.
          </p>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
          <ChevronDown className="w-8 h-8 text-white/60" />
        </div>
      </header>

      <main className="relative">
        {/* ROI / Capacity Section */}
        <section id="roi" className="py-20 px-4 bg-white scroll-mt-16">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-5xl font-bold text-slate-900 mb-4 text-center">The Capacity Math</h2>
            <p className="text-xl text-slate-600 text-center mb-10 max-w-4xl mx-auto">
              Large firms don’t lose because they lack demand. They lose because time gets burned on precedent hunting,
              document assembly, version chaos, and internal coordination. A secure firm brain compresses that overhead.
            </p>

            {/* Interactive sliders */}
            <div className="bg-slate-50 border border-slate-200 rounded-3xl p-8 mb-10">
              <div className="flex items-start gap-4 mb-6">
                <Calculator className="w-7 h-7 text-sky-700 mt-1" />
                <div>
                  <p className="text-2xl font-bold text-slate-900 mb-1">Set the assumptions yourself</p>
                  <p className="text-slate-600">
                    Drag the sliders the way a partner would in a meeting. This models{" "}
                    <strong>billable capacity unlocked</strong>, not guaranteed revenue.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Lawyer count */}
                <div className="bg-white border border-slate-200 rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-bold text-slate-600 uppercase">Lawyers (model)</p>
                    <span className="text-sm font-semibold text-slate-900">{lawyerCount}</span>
                  </div>
                  <input
                    type="range"
                    min={50}
                    max={200}
                    step={1}
                    value={lawyerCount}
                    onChange={(e) => setLawyerCount(Number(e.target.value))}
                    className="w-full"
                    aria-label="Lawyer count"
                  />
                  <p className="text-xs text-slate-500 mt-2">Reference size: {firm.lawyerRangeLabel}</p>
                </div>

                {/* Hours saved */}
                <div className="bg-white border border-slate-200 rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-bold text-slate-600 uppercase">Hours saved / lawyer / month</p>
                    <span className="text-sm font-semibold text-slate-900">{hoursSavedPerLawyerPerMonth} hrs</span>
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={30}
                    step={1}
                    value={hoursSavedPerLawyerPerMonth}
                    onChange={(e) => setHoursSavedPerLawyerPerMonth(Number(e.target.value))}
                    className="w-full"
                    aria-label="Hours saved per lawyer per month"
                  />
                  <p className="text-xs text-slate-500 mt-2">Typical range: 8–18 hrs with solid adoption</p>
                </div>

                {/* Rate */}
                <div className="bg-white border border-slate-200 rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-bold text-slate-600 uppercase">Blended billable rate</p>
                    <span className="text-sm font-semibold text-slate-900">{formatCAD(blendedRateCAD)}/hr</span>
                  </div>
                  <input
                    type="range"
                    min={150}
                    max={900}
                    step={25}
                    value={blendedRateCAD}
                    onChange={(e) => setBlendedRateCAD(Number(e.target.value))}
                    className="w-full"
                    aria-label="Blended billable rate"
                  />
                  <p className="text-xs text-slate-500 mt-2">Adjust for your partner/associate mix</p>
                </div>
              </div>

              {/* Outputs */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8">
                <div className="bg-white border border-slate-200 rounded-2xl p-6">
                  <p className="text-slate-500 mb-2">Hours unlocked</p>
                  <p className="text-4xl font-bold text-slate-900">{monthlyHoursUnlocked.toLocaleString("en-CA")}</p>
                  <p className="text-slate-600 mt-2">per month</p>
                </div>

                <div className="bg-white border border-slate-200 rounded-2xl p-6">
                  <p className="text-slate-500 mb-2">Capacity value</p>
                  <p className="text-4xl font-bold text-slate-900">{formatCAD(monthlyCapacityValue)}</p>
                  <p className="text-slate-600 mt-2">per month</p>
                </div>

                <div className="bg-gradient-to-br from-emerald-600 to-sky-600 rounded-2xl p-6 text-white">
                  <p className="text-emerald-100 mb-2">Annual capacity value</p>
                  <p className="text-4xl font-bold">{formatCAD(annualCapacityValue)}</p>
                  <p className="text-emerald-50 mt-2">capacity unlocked (not guaranteed revenue)</p>
                </div>
              </div>
            </div>

            {/* Scenario cards */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
              <div className="flex items-start gap-4 mb-6">
                <TrendingUp className="w-7 h-7 text-sky-700 mt-1" />
                <div>
                  <p className="text-2xl font-bold text-slate-900 mb-1">Credible ranges (no “magic” number)</p>
                  <p className="text-slate-600">
                    These scenarios use your current lawyer count and blended rate. Only the hours-saved assumption
                    varies.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {scenarios.map((s) => (
                  <div
                    key={s.label}
                    className={[
                      "rounded-2xl p-6 border transition-all",
                      s.highlight
                        ? "bg-gradient-to-br from-sky-50 to-indigo-50 border-sky-300 shadow-md"
                        : "bg-slate-50 border-slate-200",
                    ].join(" ")}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-bold text-slate-600 uppercase">{s.label}</p>
                      {s.highlight ? (
                        <span className="text-xs font-semibold px-2 py-1 rounded-full bg-sky-600 text-white">
                          Current assumption
                        </span>
                      ) : null}
                    </div>

                    <p className="text-3xl font-bold text-slate-900 mb-1">
                      {s.mHours.toLocaleString("en-CA")} hrs/month
                    </p>
                    <p className="text-slate-600 mb-4">{s.note}</p>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-600">Monthly value</span>
                        <span className="text-sm font-semibold text-slate-900">{formatCAD(s.mValue)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-600">Annual value</span>
                        <span className="text-sm font-semibold text-slate-900">{formatCAD(s.yValue)}</span>
                      </div>
                      <div className="pt-3 border-t border-slate-200">
                        <span className="text-xs text-slate-500">
                          Assumes {s.hoursSaved} hrs/lawyer/month × {lawyerCount} lawyers × {formatCAD(blendedRateCAD)}
                          /hr
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <p className="text-xs text-slate-500 mt-6">
                Note: impact depends on demand, utilization, realization, practice mix, and your approval policy. This
                is a “capacity unlocked” model.
              </p>
            </div>
          </div>
        </section>

        {/* What it actually does */}
        <section className="py-20 px-4 bg-gradient-to-b from-slate-50 to-white">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-5xl font-bold text-slate-900 mb-4 text-center">Not a Chatbot. A Secure Firm Brain.</h2>
            <p className="text-xl text-slate-600 text-center mb-14 max-w-3xl mx-auto">
              Lexa doesn’t “suggest.” It <strong>assembles work from your precedent</strong>, runs workflows, and
              coordinates matters — all with permission and auditability.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              {whatItDoes.map((item, idx) => (
                <div
                  key={idx}
                  className="bg-white border-2 border-sky-200 rounded-xl p-6 hover:shadow-lg transition-all"
                >
                  <div className="flex items-start gap-4">
                    <CheckCircle className="w-6 h-6 text-sky-700 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-xl font-bold text-slate-900 mb-2">{item.action}</h3>
                      <p className="text-slate-600">{item.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-gradient-to-r from-slate-900 to-indigo-950 text-white rounded-2xl p-8 text-center border border-white/10">
              <p className="text-2xl font-semibold mb-2">The Real Difference?</p>
              <p className="text-3xl font-bold">
                Generic AI drafts text. <span className="text-sky-300">Lexa drafts with your firm’s precedent.</span>
              </p>
            </div>
          </div>
        </section>

        {/* How it grows */}
        <section className="py-20 px-4 bg-slate-50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-5xl font-bold text-slate-900 mb-4 text-center">How Lexa Scales With the Firm</h2>
            <p className="text-xl text-slate-600 text-center mb-16 max-w-3xl mx-auto">
              Start with a secure firm vault. Then precedent-driven drafting. Then workflow execution — always governed
              by your rules.
            </p>

            <div className="space-y-8">
              {howItGrows.map((phase, idx) => {
                const Icon = phase.icon;
                return (
                  <div
                    key={idx}
                    className="bg-white rounded-2xl p-8 shadow-md border border-slate-200 overflow-hidden hover:shadow-lg transition-all"
                  >
                    <div className="flex items-start gap-6">
                      <div className="flex-shrink-0">
                        <div className="p-3 bg-gradient-to-br from-sky-100 to-indigo-100 rounded-lg">
                          <Icon className="w-8 h-8 text-sky-700" />
                        </div>
                      </div>
                      <div className="flex-grow">
                        <div className="flex items-baseline gap-3 mb-2">
                          <span className="text-sm font-bold text-sky-700 uppercase">{phase.week}</span>
                          <h3 className="text-2xl font-bold text-slate-900">{phase.title}</h3>
                        </div>
                        <p className="text-slate-600 mb-4 text-lg">{phase.description}</p>
                        <div className="bg-slate-50 border-l-4 border-indigo-400 p-4 rounded italic text-slate-700">
                          {phase.example}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* You + Lexa */}
        <section className="py-20 px-4 bg-white">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-5xl font-bold text-slate-900 mb-4 text-center">Lawyers + Lexa</h2>
            <p className="text-xl text-slate-600 text-center mb-16 max-w-3xl mx-auto">
              Lawyers do judgment and strategy. Lexa does the assembly and coordination — securely and consistently.
            </p>

            <div className="space-y-6">
              {partnership.map((pair, idx) => (
                <div key={idx} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gradient-to-br from-sky-50 to-sky-100 border-2 border-sky-300 rounded-2xl p-8">
                    <p className="text-sm font-bold text-sky-700 uppercase mb-2">You</p>
                    <p className="text-2xl font-bold text-slate-900 mb-4">{pair.you}</p>
                    <p className="text-slate-700 italic">{pair.example}</p>
                  </div>
                  <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-2 border-indigo-300 rounded-2xl p-8">
                    <p className="text-sm font-bold text-indigo-700 uppercase mb-2">Lexa</p>
                    <p className="text-2xl font-bold text-slate-900 mb-4">{pair.lexa}</p>
                    <p className="text-slate-700 italic">{pair.example2}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Core capabilities */}
        <section className="py-20 px-4 bg-slate-50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-5xl font-bold text-slate-900 mb-16 text-center">What Makes This Work</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {capabilities.map((cap, idx) => {
                const Icon = cap.icon;
                return (
                  <div
                    key={idx}
                    className="bg-white rounded-2xl p-8 shadow-md hover:shadow-lg hover:-translate-y-2 transition-all border border-slate-200"
                  >
                    <div className="mb-4 p-3 bg-gradient-to-br from-sky-100 to-indigo-100 rounded-lg w-fit">
                      <Icon className="w-6 h-6 text-sky-700" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-3">{cap.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{cap.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Security model */}
        <section className="py-20 px-4 bg-white">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900 text-white mb-4">
                <Shield className="w-4 h-4" />
                <span className="text-sm font-semibold">Security First</span>
              </div>
              <h2 className="text-5xl font-bold text-slate-900 mb-4">Built for Privileged Work</h2>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                Law firms don’t get second chances on confidentiality. Lexa is designed with isolation, permissions, and
                auditability at the core.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {securityPillars.map((p, idx) => {
                const Icon = p.icon;
                return (
                  <div
                    key={idx}
                    className="bg-slate-50 rounded-2xl p-8 border border-slate-200 shadow-sm hover:shadow-md transition-all"
                  >
                    <div className="mb-4 p-3 bg-gradient-to-br from-emerald-100 to-sky-100 rounded-lg w-fit">
                      <Icon className="w-6 h-6 text-emerald-700" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">{p.title}</h3>
                    <p className="text-slate-600">{p.description}</p>
                  </div>
                );
              })}
            </div>

            <div className="mt-12 bg-slate-900 text-white rounded-2xl p-8 border border-white/10">
              <div className="flex items-start gap-4">
                <Building2 className="w-7 h-7 text-sky-300 mt-1" />
                <div>
                  <p className="text-2xl font-semibold mb-2">Firm-Isolated by Default</p>
                  <p className="text-slate-200 leading-relaxed">
                    No public training on your documents. No cross-firm memory. Permissions and matter walls govern what
                    Lexa can see and do — and every action is logged.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 px-4 bg-gradient-to-br from-sky-600 via-indigo-600 to-slate-900">
          <div className="max-w-5xl mx-auto text-center text-white">
            <h2 className="text-5xl md:text-6xl font-bold mb-6">Start With a Private Pilot</h2>
            <p className="text-2xl mb-4">
              We’ll validate security, prove the ROI, and implement 2–3 high-leverage workflows.
            </p>
            <p className="text-lg mb-10 text-white/80">
              Start small. Prove value. Expand by practice group — without compromising privilege.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-3">
              <a
                href="#roi"
                className="px-10 py-5 bg-white/10 border border-white/20 text-white text-xl font-bold rounded-full hover:bg-white/15 transition-all flex items-center justify-center gap-2"
              >
                Re-run the ROI Model <Calculator className="w-6 h-6" />
              </a>
            </div>

            <p className="text-white/80 text-sm">
              ✅ Permissioned execution • ✅ Audit trails • ✅ Firm-isolated memory • ✅ Matter walls
            </p>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-slate-950 text-white py-12 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-lg font-semibold mb-2">Lexa — Your Firm’s Brain</p>
          <p className="text-slate-400 mb-2">Secure collective intelligence for modern law firms.</p>
          <p className="text-slate-500 text-sm mt-6">© 2026. All rights reserved.</p>
        </div>
      </footer>

      <style>{`
        @keyframes blob {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }
        .animate-blob { animation: blob 7s infinite; }
        .animation-delay-2000 { animation-delay: 2s; }
      `}</style>
    </div>
  );
}
